import { createBrowserRouter, RouterProvider, Outlet, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState, type ComponentType } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { EmailProvider } from './contexts/EmailContext';
import Layout from './components/Layout';
import AuthenticatedOutlet from './components/AuthenticatedOutlet';
import Login from './pages/Login';
import { useAuth } from './contexts/AuthContext';
import { activePortalRegion, canAccessPortalRegion, getPortalRegions } from './config/portal-regions';
import { usePermissions } from './hooks/usePermissions';
import { apiService } from './services/api';
import { sharedSecretReadPermissions } from './utils/secret-sharing';
import { Toaster, ToastBar, toast } from 'react-hot-toast';
import { X } from 'lucide-react';
import CookieConsent from './components/CookieConsent';
import { RouteErrorPage } from './components/ErrorBoundary';
import { AiAssistantProvider } from './contexts/AiAssistantProvider';
import { RefreshShortcutProvider } from './contexts/RefreshShortcutProvider';
import './utils/keyboard-shortcut';
import {
  clearAuthReturn,
  isPostAuthBouncePath,
  rememberAuthReturn,
  resolvePostAuthRedirect,
} from './utils/authPaths';

// Lazy load all pages

/* eslint-disable @typescript-eslint/no-explicit-any */
const CHUNK_RELOAD_KEY = 'simply-send-chunk-reload';

const lazyWithRetry = (
  importFn: () => Promise<{ default: ComponentType<any> }>,
  retries = 2
) => {
  const load = (attemptsLeft: number): Promise<{ default: ComponentType<any> }> =>
    importFn()
      .then((module) => {
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        }
        return module;
      })
      .catch((error: Error) => {
        const isChunkError =
          /Loading chunk .* failed/i.test(error.message) ||
          /Failed to fetch/i.test(error.message) ||
          /dynamic import/i.test(error.message);

        if (isChunkError && attemptsLeft > 0) {
          return new Promise<void>((resolve) => setTimeout(() => resolve(), 500)).then(() =>
            load(attemptsLeft - 1)
          );
        }

        if (
          isChunkError &&
          typeof window !== 'undefined' &&
          window.sessionStorage.getItem(CHUNK_RELOAD_KEY) !== 'true'
        ) {
          window.sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true');
          window.location.reload();
          return new Promise<{ default: ComponentType<any> }>(() => {
            // Keep Suspense pending while the browser reloads the current document.
          });
        }

        throw error;
      });
  return lazy(() => load(retries));
};

const HomePage = lazyWithRetry(() => import('./pages/HomePage'));
const AnalyticsDashboard = lazyWithRetry(() => import('./pages/AnalyticsDashboard'));
const LiveEmailScreensaver = lazyWithRetry(() => import('./pages/LiveEmailScreensaver'));
const EmailEventsPage = lazyWithRetry(() => import('./pages/EmailEventsPage'));
const DomainManagement = lazyWithRetry(() => import('./pages/DomainManagement'));
const DnsRecordsPage = lazyWithRetry(() => import('./pages/DnsRecordsPage'));
const BimiSetupPage = lazyWithRetry(() => import('./pages/BimiSetupPage'));
const IPManagement = lazyWithRetry(() => import('./pages/IPManagement'));
const SharedSecrets = lazyWithRetry(() => import('./pages/SharedSecrets'));
const APIKeys = lazyWithRetry(() => import('./pages/APIKeys'));
const SMTPCredentials = lazyWithRetry(() => import('./pages/SMTPCredentials'));
const AccountPage = lazyWithRetry(() => import('./pages/AccountPage'));
const PricingPage = lazyWithRetry(() => import('./pages/PricingPage'));
const Templates = lazyWithRetry(() => import('./pages/UserTemplates'));
const TemplateEditorPage = lazyWithRetry(() => import('./pages/TemplateEditorPage'));
const TemplateRepository = lazyWithRetry(() => import('./pages/TemplateRepository'));
const Webhooks = lazyWithRetry(() => import('./pages/Webhooks'));
const Integrations = lazyWithRetry(() => import('./pages/Integrations'));
const McpIntegration = lazyWithRetry(() => import('./pages/McpIntegration'));
const McpAuthorize = lazyWithRetry(() => import('./pages/McpAuthorize'));
const GoogleContactsIntegration = lazyWithRetry(() => import('./pages/GoogleContactsIntegration'));
const GoogleSheetsIntegration = lazyWithRetry(() => import('./pages/GoogleSheetsIntegration'));
const GoogleFormsIntegration = lazyWithRetry(() => import('./pages/GoogleFormsIntegration'));
const ShopifyIntegration = lazyWithRetry(() => import('./pages/ShopifyIntegration'));
const ZapierIntegration = lazyWithRetry(() => import('./pages/ZapierIntegration'));
const SupabaseIntegration = lazyWithRetry(() => import('./pages/SupabaseIntegration'));
const ShopifyAppHome = lazyWithRetry(() => import('./pages/ShopifyAppHome'));
const ShopifyOAuthCallback = lazyWithRetry(() => import('./pages/ShopifyOAuthCallback'));
const ShopifyAppPricingComplete = lazyWithRetry(() => import('./pages/ShopifyAppPricingComplete'));
const ReferralPage = lazyWithRetry(() => import('./pages/ReferralPage'));
const DonatePage = lazyWithRetry(() => import('./pages/DonatePage'));
const MailboxPage = lazyWithRetry(() => import('./pages/Mailbox'));
const SandboxInboxPage = lazyWithRetry(() => import('./pages/SandboxInboxPage'));
const SubscriptionGroups = lazyWithRetry(() => import('./pages/SubscriptionGroups'));
const SubscriptionGroupDetails = lazyWithRetry(() => import('./pages/SubscriptionGroupDetails'));
const ContactsPage = lazyWithRetry(() => import('./pages/ContactsPage'));
const ContactAttributesPage = lazyWithRetry(() => import('./pages/ContactAttributesPage'));
const SegmentsPage = lazyWithRetry(() => import('./pages/SegmentsPage'));
const SegmentEditorPage = lazyWithRetry(() => import('./pages/SegmentEditorPage'));
const CSVUploadFlowPage = lazyWithRetry(() => import('./pages/CSVUploadFlowPage'));
const AzureLanding = lazyWithRetry(() => import('./pages/AzureLanding'));
const AWSMarketplaceLanding = lazyWithRetry(() => import('./pages/AWSMarketplaceLanding'));
const SuppressionList = lazyWithRetry(() => import('./pages/SuppressionList'));
const Workflows = lazyWithRetry(() => import('./pages/Workflows'));
const WorkflowEditor = lazyWithRetry(() => import('./pages/WorkflowEditor'));
const WorkflowStatsPage = lazyWithRetry(() => import('./pages/WorkflowStatsPage'));
const EmailCampaigns = lazyWithRetry(() => import('./pages/EmailCampaigns'));
const CampaignExecutionPage = lazyWithRetry(() => import('./pages/CampaignExecutionPage'));
const AcceptInvitationPage = lazyWithRetry(() => import('./pages/AcceptInvitationPage'));

// const env = import.meta.env.VITE_APP_ENV || 'production';

function PermissionRoute({ permission, any = false, children }: { permission: string | string[]; any?: boolean; children: React.ReactNode }) {
  const { hasAllPermissions, hasAnyPermission } = usePermissions();
  const required = Array.isArray(permission) ? permission : [permission];
  if ((any ? !hasAnyPermission(required) : !hasAllPermissions(required))) return <Navigate to="/" replace />;
  return <>{children}</>;
}

const requirePermission = (permission: string | string[], page: React.ReactNode) => (
  <PermissionRoute permission={permission}>{page}</PermissionRoute>
);
const requireAnyPermission = (permission: string[], page: React.ReactNode) => (
  <PermissionRoute permission={permission} any>{page}</PermissionRoute>
);

/** After login/signup on an auth URL, restore deep-link path+query (redirect param / session). */
function PostAuthRedirect() {
  const location = useLocation();
  // useState initializer avoids Strict Mode double-consume wiping the return URL.
  const [to] = useState(() => resolvePostAuthRedirect(location.search));

  useEffect(() => {
    clearAuthReturn();
  }, []);

  return <Navigate to={to} replace />;
}

function SMTPWorkspaceRoute() {
  const { workspaceId } = usePermissions();
  return <SMTPCredentials key={workspaceId} />;
}

function SharedSecretsWorkspaceRoute() {
  const { workspaceId } = usePermissions();
  const { user } = useAuth();
  return <SharedSecrets key={`${workspaceId}:${user?.id || ''}`} />;
}

function SuppressionWorkspaceRoute() {
  const { workspaceId } = usePermissions();
  const { user } = useAuth();
  return <SuppressionList key={`${workspaceId}:${user?.id || ''}`} />;
}

function MailboxWorkspaceRoute({ virtual = false }: { virtual?: boolean }) {
  const { user } = useAuth();
  const activeWorkspaceType = user?.workspaces?.find(
    (workspace) => workspace.workspaceId === apiService.getWorkspaceId()
  )?.workspaceType ?? 'live';

  if (activeWorkspaceType === 'sandbox' && !virtual) {
    return <Navigate to="/virtual-inbox" replace />;
  }
  if (activeWorkspaceType !== 'sandbox' && virtual) {
    return <Navigate to="/mailbox" replace />;
  }

  return virtual
    ? requirePermission('email_event:read', <Suspense fallback={<PageLoader />}><SandboxInboxPage /></Suspense>)
    : requireAnyPermission(['inbox:read', 'email:send'], <Suspense fallback={<PageLoader />}><MailboxPage /></Suspense>);
}

// Loading fallback for lazy-loaded routes
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-black">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600"></div>
    </div>
  );
}

function AppLayout() {
  const { user, loading, authReady } = useAuth();
  const location = useLocation();

  // While logged out on a deep link, remember full path+query for post-auth return.
  useEffect(() => {
    if (loading || user) return;
    rememberAuthReturn(location.pathname, location.search);
  }, [loading, user, location.pathname, location.search]);

  // Auth completed while already on the deep link (never left /azure/landing) —
  // drop the saved return so a later /sign-in visit does not reuse a stale token URL.
  useEffect(() => {
    if (loading || !user) return;
    if (!isPostAuthBouncePath(location.pathname)) {
      clearAuthReturn();
    }
  }, [loading, user, location.pathname]);

  if (!authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <CookieConsent />
        <Login />
      </>
    );
  }

  if (!canAccessPortalRegion(activePortalRegion, user.email)) {
    const us = getPortalRegions().find(region => region.id === 'us' && region.enabled);
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-white text-zinc-900 dark:bg-black dark:text-zinc-100">
        <p><span>This region is currently available to internal users only.</span></p>
        {us && <a className="text-cyan-600 hover:underline dark:text-cyan-400" href={`${us.origin}/sign-in`}><span>Continue in United States</span></a>}
      </div>
    );
  }

  return (
    <>
      <CookieConsent />
      <Layout>
        <Outlet />
      </Layout>
    </>
  );
}

const router = createBrowserRouter([
  {
    path: '/shopify/auth/callback',
    element: <Suspense fallback={<PageLoader />}><ShopifyOAuthCallback /></Suspense>,
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/shopify',
    element: <Suspense fallback={<PageLoader />}><ShopifyAppHome /></Suspense>,
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/shopify/billing-complete',
    element: <Suspense fallback={<PageLoader />}><ShopifyAppPricingComplete /></Suspense>,
    errorElement: <RouteErrorPage />,
  },
  {
    element: <AppLayout />,
    children: [
      {
        element: <AuthenticatedOutlet />,
        errorElement: <RouteErrorPage />,
        children: [
      { path: '/', element: <Suspense fallback={<PageLoader />}><HomePage /></Suspense> },
      { path: '/inbox', element: <Navigate to="/mailbox" replace /> },
      { path: '/mailbox', element: <MailboxWorkspaceRoute /> },
      { path: '/virtual-inbox', element: <MailboxWorkspaceRoute virtual /> },
      { path: '/analytics', element: requirePermission('analytics:read', <Suspense fallback={<PageLoader />}><AnalyticsDashboard /></Suspense>) },
      { path: '/analytics/live', element: requirePermission('email_event:read', <Suspense fallback={<PageLoader />}><LiveEmailScreensaver /></Suspense>) },
      { path: '/email-events', element: requirePermission('email_event:read', <Suspense fallback={<PageLoader />}><EmailEventsPage /></Suspense>) },
      { path: '/domains', element: requirePermission('domain:read', <Suspense fallback={<PageLoader />}><DomainManagement /></Suspense>) },
      { path: '/domains/:domainId/dns-records', element: requirePermission('domain_dns:read', <Suspense fallback={<PageLoader />}><DnsRecordsPage /></Suspense>) },
      { path: '/domains/:domainId/bimi', element: requirePermission('domain_dns:read', <Suspense fallback={<PageLoader />}><BimiSetupPage /></Suspense>) },
      { path: '/ips', element: requirePermission(['ip_pool:read', 'team:read'], <Suspense fallback={<PageLoader />}><IPManagement /></Suspense>) },
      { path: '/shared-secrets', element: requireAnyPermission(sharedSecretReadPermissions, <Suspense fallback={<PageLoader />}><SharedSecretsWorkspaceRoute /></Suspense>) },
      { path: '/smtp', element: requirePermission('smtp:read', <Suspense fallback={<PageLoader />}><SMTPWorkspaceRoute /></Suspense>) },
      { path: '/api-keys', element: requireAnyPermission(['transactional_api_key:read', 'marketing_api_key:read', 'web_api_key:read'], <Suspense fallback={<PageLoader />}><APIKeys /></Suspense>) },
      { path: '/templates', element: requirePermission('email_template:read', <Suspense fallback={<PageLoader />}><Templates /></Suspense>) },
      { path: '/templates/:templateId/:mode?', element: requirePermission('email_template:write', <Suspense fallback={<PageLoader />}><TemplateEditorPage /></Suspense>) },
      { path: '/template-repository', element: requirePermission('factory_email_template:read', <Suspense fallback={<PageLoader />}><TemplateRepository /></Suspense>) },
      { path: '/contacts', element: requirePermission('contact:read', <Suspense fallback={<PageLoader />}><ContactsPage /></Suspense>) },
      { path: '/contacts/upload', element: requirePermission('contact:write', <Suspense fallback={<PageLoader />}><CSVUploadFlowPage /></Suspense>) },
      { path: '/contacts/directory/upload', element: requirePermission('contact:write', <Suspense fallback={<PageLoader />}><CSVUploadFlowPage /></Suspense>) },
      { path: '/contacts/:subscriptionGroupId/upload', element: requirePermission('contact:write', <Suspense fallback={<PageLoader />}><CSVUploadFlowPage /></Suspense>) },
      { path: '/contacts/custom-fields', element: requirePermission('contact:read', <Suspense fallback={<PageLoader />}><ContactAttributesPage /></Suspense>) },
      { path: '/contacts/subscription-groups', element: requirePermission('subscription_group:read', <Suspense fallback={<PageLoader />}><SubscriptionGroups /></Suspense>) },
      { path: '/contacts/subscription-groups/:subscriptionGroupId', element: requirePermission('subscription_group:read', <Suspense fallback={<PageLoader />}><SubscriptionGroupDetails /></Suspense>) },
      { path: '/contacts/segments', element: requirePermission('segment:read', <Suspense fallback={<PageLoader />}><SegmentsPage /></Suspense>) },
      { path: '/contacts/segments/new', element: requirePermission('segment:write', <Suspense fallback={<PageLoader />}><SegmentEditorPage /></Suspense>) },
      { path: '/contacts/segments/:segmentId/edit', element: requirePermission('segment:write', <Suspense fallback={<PageLoader />}><SegmentEditorPage /></Suspense>) },
      { path: '/webhooks', element: requirePermission('webhook:read', <Suspense fallback={<PageLoader />}><Webhooks /></Suspense>) },
      { path: '/campaigns', element: requirePermission('campaign:read', <Suspense fallback={<PageLoader />}><EmailCampaigns /></Suspense>) },
      { path: '/campaigns/new/edit', element: requirePermission('campaign:write', <Suspense fallback={<PageLoader />}><CampaignExecutionPage /></Suspense>) },
      { path: '/campaigns/:campaignId', element: requirePermission('campaign:read', <Suspense fallback={<PageLoader />}><CampaignExecutionPage /></Suspense>) },
      { path: '/campaigns/:campaignId/edit', element: requirePermission('campaign:read', <Suspense fallback={<PageLoader />}><CampaignExecutionPage /></Suspense>) },
      { path: '/campaigns/:campaignId/schedule', element: requirePermission('campaign:read', <Suspense fallback={<PageLoader />}><CampaignExecutionPage /></Suspense>) },
      { path: '/integrations', element: requirePermission('integration:read', <Suspense fallback={<PageLoader />}><Integrations /></Suspense>) },
      { path: '/integrations/mcp', element: requirePermission('integration:read', <Suspense fallback={<PageLoader />}><McpIntegration /></Suspense>) },
      { path: '/integrations/mcp/authorize', element: requirePermission('integration:write', <Suspense fallback={<PageLoader />}><McpAuthorize /></Suspense>) },
      { path: '/integrations/mcp/:platform', element: requirePermission('integration:read', <Suspense fallback={<PageLoader />}><McpIntegration /></Suspense>) },
      { path: '/integrations/google-contacts', element: requirePermission('integration:write', <Suspense fallback={<PageLoader />}><GoogleContactsIntegration /></Suspense>) },
      { path: '/integrations/google-contacts/callback', element: requirePermission('integration:write', <Suspense fallback={<PageLoader />}><GoogleContactsIntegration /></Suspense>) },
      { path: '/integrations/google-sheets', element: requirePermission('integration:write', <Suspense fallback={<PageLoader />}><GoogleSheetsIntegration /></Suspense>) },
      { path: '/integrations/google-sheets/callback', element: requirePermission('integration:write', <Suspense fallback={<PageLoader />}><GoogleSheetsIntegration /></Suspense>) },
      { path: '/integrations/google-forms', element: requirePermission('integration:write', <Suspense fallback={<PageLoader />}><GoogleFormsIntegration /></Suspense>) },
      { path: '/integrations/google-forms/callback', element: requirePermission('integration:write', <Suspense fallback={<PageLoader />}><GoogleFormsIntegration /></Suspense>) },
      { path: '/integrations/shopify', element: requirePermission('integration:write', <Suspense fallback={<PageLoader />}><ShopifyIntegration /></Suspense>) },
      { path: '/integrations/zapier', element: requirePermission('integration:write', <Suspense fallback={<PageLoader />}><ZapierIntegration /></Suspense>) },
      { path: '/integrations/supabase', element: requirePermission('integration:write', <Suspense fallback={<PageLoader />}><SupabaseIntegration /></Suspense>) },
      { path: '/integrations/supabase/callback', element: requirePermission('integration:write', <Suspense fallback={<PageLoader />}><SupabaseIntegration /></Suspense>) },
      { path: '/contacts/suppressions', element: requirePermission('suppression:read', <Suspense fallback={<PageLoader />}><SuppressionWorkspaceRoute /></Suspense>) },
      { path: '/referral', element: <Suspense fallback={<PageLoader />}><ReferralPage /></Suspense> },
      { path: '/account', element: <Suspense fallback={<PageLoader />}><AccountPage /></Suspense> },
      { path: '/pricing', element: requirePermission('billing:read', <Suspense fallback={<PageLoader />}><PricingPage /></Suspense>) },
      { path: '/donate', element: <Suspense fallback={<PageLoader />}><DonatePage /></Suspense> },
      { path: '/azure/landing', element: <Suspense fallback={<PageLoader />}><AzureLanding /></Suspense> },
      { path: '/aws-marketplace/landing', element: <Suspense fallback={<PageLoader />}><AWSMarketplaceLanding /></Suspense> },
      { path: '/workflows', element: requirePermission('workflow:read', <Suspense fallback={<PageLoader />}><Workflows /></Suspense>) },
      { path: '/workflows/:workflowId', element: <Navigate to="stats" replace /> },
      { path: '/workflows/:workflowId/stats', element: requirePermission('workflow:read', <Suspense fallback={<PageLoader />}><WorkflowStatsPage /></Suspense>) },
      { path: '/workflows/:workflowId/edit', element: requirePermission('workflow:write', <Suspense fallback={<PageLoader />}><WorkflowEditor /></Suspense>) },
      { path: '/accept-invitation', element: <Suspense fallback={<PageLoader />}><AcceptInvitationPage /></Suspense> },
      // Auth entry paths: when logged in, restore deep-link return (or home).
      // When logged out, AppLayout renders Login and these elements are not shown.
      { path: '/sign-up', element: <PostAuthRedirect /> },
      { path: '/sign-in', element: <PostAuthRedirect /> },
      { path: '/login', element: <PostAuthRedirect /> },
      { path: '/signup', element: <PostAuthRedirect /> },
      { path: '/auth/login', element: <PostAuthRedirect /> },
      { path: '/auth/signup', element: <PostAuthRedirect /> },
      { path: '/forgot-password', element: <PostAuthRedirect /> },
      { path: '/reset-password', element: <PostAuthRedirect /> },
      { path: '/confirm-email', element: <PostAuthRedirect /> },
      { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <EmailProvider>
        <AiAssistantProvider>
          <RefreshShortcutProvider>
            <Toaster
              containerStyle={{ zIndex: 2147483647 }}
              toastOptions={{
                duration: 5000,
                position: 'top-right',
                style: {
                  background: 'var(--toast-bg)',
                  color: 'var(--toast-color)',
                  border: '1px solid var(--toast-border)',
                  borderRadius: '12px',
                  fontSize: '14px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                },
                success: {
                  iconTheme: {
                    primary: '#06b6d4',
                    secondary: '#fff',
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                }
              }}
            >
              {(t) => (
                <ToastBar toast={t}>
                  {({ icon, message }) => (
                    <>
                      {icon}
                      {message}
                      {t.type !== 'loading' && (
                        <button
                          onClick={() => toast.dismiss(t.id)}
                          className="ml-2 -mr-1 p-0.5 rounded-full text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors focus:outline-none shrink-0"
                          title="Dismiss notification"
                        >
                          <X size={14} className="stroke-[2.5]" />
                        </button>
                      )}
                    </>
                  )}
                </ToastBar>
              )}
            </Toaster>
            <RouterProvider router={router} />
          </RefreshShortcutProvider>
        </AiAssistantProvider>
      </EmailProvider>
    </QueryClientProvider>
  );
}

export default App;
